// The in-memory fake: a `Games` over a MemoryStorage, laid out the way a
// real provider lays games out, so that consumers' tests and the contract
// exercise the same folder shape.
import { MemoryStorage } from '@chess/api-storage/memory';
import type { Storage } from '@chess/api-storage';
import type { CountOptions, Games, GamesEvent, ListOptions } from './index.ts';
import type { GameId, GameRecord, GameSummary } from '@chess/api-model';
import { GAME_PGN_FILE, GAME_RECORD_FILE } from './types.ts';
import { parseGameRecord, summarize } from './schema.ts';
import { matchesFilter } from './filter.ts';
import type { GameFilter } from '@chess/api-model';

export class MemoryGames implements Games {
  private listeners = new Set<(event: GamesEvent) => void>();
  private readonly storage: Storage;

  constructor(storage: Storage = new MemoryStorage()) {
    this.storage = storage;
  }

  private gameDir(id: GameId): string {
    return `games/${id}`;
  }

  async list(options: ListOptions = {}): Promise<GameSummary[]> {
    const summaries = await this.summaries(options.owner, options.filter);
    const sign = options.order === 'oldest' ? 1 : -1;
    summaries.sort((a, b) => sign * a.playedAt.localeCompare(b.playedAt));
    const offset = Math.max(0, options.offset ?? 0);
    const size = options.size;
    return summaries.slice(offset, size === undefined ? undefined : offset + Math.max(0, size));
  }

  async count(options: CountOptions = {}): Promise<number> {
    return (await this.summaries(options.owner, options.filter)).length;
  }

  async records(options: { since?: string } = {}): Promise<GameRecord[]> {
    const since = options.since === undefined ? undefined : Date.parse(options.since);
    const entries = await this.storage.list('games');
    const records: GameRecord[] = [];
    for (const e of entries) {
      if (e.kind !== 'dir') continue;
      const record = await this.get(e.name);
      if (record && (since === undefined || Date.parse(record.playedAt) > since)) records.push(record);
    }
    return records;
  }

  /** Every stored game's summary, optionally one owner's and matching a filter, unordered. */
  private async summaries(owner?: string, filter?: GameFilter): Promise<GameSummary[]> {
    const entries = await this.storage.list('games');
    const summaries: GameSummary[] = [];
    for (const e of entries) {
      if (e.kind !== 'dir') continue;
      const text = await this.storage.readText(`${this.gameDir(e.name)}/${GAME_RECORD_FILE}`);
      if (text === undefined) continue;
      const record = parseGameRecord(JSON.parse(text));
      if (owner && record.owner !== owner) continue;
      const summary = summarize(record);
      if (filter && !matchesFilter(record, filter)) continue;
      summaries.push(summary);
    }
    return summaries;
  }

  async get(id: GameId): Promise<GameRecord | undefined> {
    const text = await this.storage.readText(`${this.gameDir(id)}/${GAME_RECORD_FILE}`);
    return text === undefined ? undefined : parseGameRecord(JSON.parse(text));
  }

  async put(record: GameRecord): Promise<void> {
    const dir = this.gameDir(record.id);
    await this.storage.write(`${dir}/${GAME_RECORD_FILE}`, JSON.stringify(record));
    await this.storage.write(`${dir}/${GAME_PGN_FILE}`, record.pgn);
    this.emit({ type: 'added', game: summarize(record) });
  }

  async remove(id: GameId): Promise<void> {
    if (!(await this.storage.exists(`${this.gameDir(id)}/${GAME_RECORD_FILE}`))) return;
    await this.storage.remove(this.gameDir(id));
    this.emit({ type: 'removed', id });
  }

  folder(id: GameId, plugin: string): Storage {
    return this.storage.scope(`${this.gameDir(id)}/${plugin}`);
  }

  subscribe(listener: (event: GamesEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: GamesEvent): void {
    for (const l of this.listeners) l(event);
  }
}
